import React, { useState, useEffect } from 'react';
import {
  FaArrowLeft, FaStar, FaGift, FaHistory, FaCopy, FaCheck, FaUsers,
  FaChevronRight, FaLock, FaExchangeAlt, FaAward, FaTrophy, FaShareAlt,
  FaSpinner, FaRocket, FaInfoCircle, FaEnvelope, FaPaperPlane
} from 'react-icons/fa';
import {
  getPointsBalance, getPointsHistory, getReferralLink, getReferralHistory,
  getRewardsCatalog, redeemPoints, sendReferralInvite
} from '../api/client';

const TIER_STYLES = {
  explorer: { bg: '#F3F4F6', border: '#9CA3AF', gradient: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)', icon: '\u{1F331}' },
  navigator: { bg: '#EFF6FF', border: '#60A5FA', gradient: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)', icon: '\u{1F9ED}' },
  adventurer: { bg: '#ECFDF5', border: '#34D399', gradient: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)', icon: '\u{1F3D4}' },
  globetrotter: { bg: '#F5F3FF', border: '#A78BFA', gradient: 'linear-gradient(135deg, #F5F3FF 0%, #EDE9FE 100%)', icon: '\u{1F30F}' },
  ambassador: { bg: '#FFFBEB', border: '#FBBF24', gradient: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)', icon: '\u2B50' },
};

const TravelPoints = ({ user, onBack, onViewPricing }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [pointsData, setPointsData] = useState(null);
  const [history, setHistory] = useState(null);
  const [referralLink, setReferralLink] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [rewards, setRewards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [redeemingId, setRedeemingId] = useState(null);
  const [redeemResult, setRedeemResult] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [showInviteForm, setShowInviteForm] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [balance, link] = await Promise.all([
        getPointsBalance(),
        getReferralLink(),
      ]);
      setPointsData(balance);
      setReferralLink(link);
    } catch (err) {
      console.error('Failed to load points data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      const data = await getPointsHistory(50, 0);
      setHistory(data);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  const loadReferrals = async () => {
    try {
      const data = await getReferralHistory();
      setReferrals(data.referrals || []);
    } catch (err) {
      console.error('Failed to load referrals:', err);
    }
  };

  const loadRewards = async () => {
    try {
      const data = await getRewardsCatalog();
      setRewards(data);
    } catch (err) {
      console.error('Failed to load rewards:', err);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'history' && !history) loadHistory();
    if (tab === 'referrals' && referrals.length === 0) loadReferrals();
    if (tab === 'redeem' && !rewards) loadRewards();
  };

  const handleCopyLink = async () => {
    if (referralLink?.referral_url) {
      await navigator.clipboard.writeText(referralLink.referral_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRedeem = async (rewardId) => {
    const reward = rewards?.rewards?.find(r => r.id === rewardId);
    const confirmMsg = reward
      ? `Are you sure you want to redeem ${reward.points_required.toLocaleString()} points for "${reward.name}"?`
      : 'Are you sure you want to redeem this reward?';
    if (!window.confirm(confirmMsg)) return;

    setRedeemingId(rewardId);
    setRedeemResult(null);
    try {
      const result = await redeemPoints(rewardId);
      setRedeemResult({ success: true, message: `${result.reward} has been applied to your account! (${result.points_spent} points used)` });
      loadData();
      loadRewards();
    } catch (err) {
      setRedeemResult({ success: false, message: err.response?.data?.detail || 'Failed to redeem' });
    } finally {
      setRedeemingId(null);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteSending(true);
    setInviteResult(null);
    try {
      await sendReferralInvite(inviteEmail.trim());
      setInviteResult({ success: true, message: `Invitation sent to ${inviteEmail.trim()}!` });
      setInviteEmail('');
      setShowInviteForm(false);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send invitation. Please try again.';
      setInviteResult({ success: false, message: detail });
    } finally {
      setInviteSending(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto flex items-center justify-center py-20">
        <FaSpinner className="animate-spin text-3xl" style={{ color: '#877B98' }} />
      </div>
    );
  }

  const tierStyle = TIER_STYLES[pointsData?.tier] || TIER_STYLES.explorer;
  const isEligible = pointsData?.eligible;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="p-2 rounded-lg transition-colors hover:bg-gray-100">
          <FaArrowLeft style={{ color: '#68625A' }} />
        </button>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#262420' }}>Travel Points</h1>
          <p style={{ color: '#8A827A' }}>Earn & redeem points for rewards</p>
        </div>
      </div>

      {/* Not eligible banner */}
      {!isEligible && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <FaLock className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-800">Upgrade to unlock Travel Points</p>
              <p className="text-sm text-amber-700 mt-1">
                The Travel Points referral and rewards program is available to Solo plan subscribers and above.
                Upgrade to start earning points through referrals, engagement, and milestones.
              </p>
              <button
                onClick={onViewPricing}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:shadow-lg"
                style={{ background: 'linear-gradient(135deg, #877B98 0%, #5C9B6E 100%)' }}
              >
                <FaRocket /> View Plans
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Points Card */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-6">
        <div className="p-6" style={{ background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Your Travel Points</h3>
            <span className="px-3 py-1 rounded-full text-sm font-medium" style={{
              background: tierStyle.border, color: 'white'
            }}>
              {tierStyle.icon} {pointsData?.tier_name}
            </span>
          </div>
          <div className="text-4xl font-bold text-emerald-400 mb-4">
            {(pointsData?.balance || 0).toLocaleString()}
            <span className="text-lg text-gray-400 ml-2">points</span>
          </div>
          {pointsData?.next_tier && (
            <>
              <div className="w-full bg-gray-600 rounded-full h-2 mb-2">
                <div className="h-2 rounded-full" style={{
                  width: `${pointsData.progress_percent}%`,
                  background: 'linear-gradient(90deg, #10B981, #F59E0B)',
                }} />
              </div>
              <p className="text-gray-400 text-sm text-right">
                {(pointsData.points_to_next_tier || 0).toLocaleString()} points to {pointsData.next_tier}
              </p>
            </>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b" style={{ borderColor: '#E5E7EB' }}>
          {[
            { id: 'overview', label: 'Overview', icon: FaStar },
            { id: 'referrals', label: 'Referrals', icon: FaUsers },
            { id: 'redeem', label: 'Redeem', icon: FaGift },
            { id: 'history', label: 'History', icon: FaHistory },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className="flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium transition-all border-b-2"
              style={{
                borderBottomColor: activeTab === tab.id ? '#877B98' : 'transparent',
                color: activeTab === tab.id ? '#877B98' : '#8A827A',
              }}
            >
              <tab.icon className="text-xs" />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl p-4" style={{ backgroundColor: '#F5F3F1' }}>
                  <p className="text-sm" style={{ color: '#8A827A' }}>Lifetime Earned</p>
                  <p className="text-xl font-bold" style={{ color: '#262420' }}>
                    {(pointsData?.lifetime_points || 0).toLocaleString()}
                  </p>
                </div>
                <div className="rounded-xl p-4" style={{ backgroundColor: '#F5F3F1' }}>
                  <p className="text-sm" style={{ color: '#8A827A' }}>Earning Rate</p>
                  <p className="text-xl font-bold" style={{ color: '#262420' }}>
                    {pointsData?.tier_multiplier || 1}x
                  </p>
                </div>
                <div className="rounded-xl p-4" style={{ backgroundColor: '#F5F3F1' }}>
                  <p className="text-sm" style={{ color: '#8A827A' }}>Friends Referred</p>
                  <p className="text-xl font-bold" style={{ color: '#262420' }}>
                    {pointsData?.referral_count || 0}
                  </p>
                </div>
                <div className="rounded-xl p-4" style={{ backgroundColor: '#F5F3F1' }}>
                  <p className="text-sm" style={{ color: '#8A827A' }}>Referral Cap Left</p>
                  <p className="text-xl font-bold" style={{ color: '#262420' }}>
                    {(pointsData?.referral_cap_remaining || 0).toLocaleString()}/mo
                  </p>
                </div>
              </div>

              {/* Referral Link */}
              {isEligible && referralLink?.referral_url && (
                <div className="rounded-xl p-4" style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <FaShareAlt style={{ color: '#3B82F6' }} />
                    <span className="font-semibold text-sm" style={{ color: '#1E40AF' }}>Your Referral Link</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={referralLink.referral_url}
                      className="flex-1 bg-white border rounded-lg px-3 py-2 text-sm"
                      style={{ borderColor: '#BFDBFE', color: '#374151' }}
                    />
                    <button
                      onClick={handleCopyLink}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all"
                      style={{ backgroundColor: copied ? '#10B981' : '#3B82F6' }}
                    >
                      {copied ? <><FaCheck className="inline mr-1" />Copied</> : <><FaCopy className="inline mr-1" />Copy</>}
                    </button>
                  </div>
                </div>
              )}

              {/* Invite a Friend */}
              {isEligible && referralLink && (
                <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #D1FAE5' }}>
                  {inviteResult && (
                    <div className={`px-4 py-3 text-sm ${inviteResult.success ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
                      {inviteResult.success ? <FaCheck className="inline mr-1.5" /> : <FaInfoCircle className="inline mr-1.5" />}
                      {inviteResult.message}
                    </div>
                  )}
                  {!showInviteForm ? (
                    <button
                      onClick={() => { setShowInviteForm(true); setInviteResult(null); }}
                      className="w-full flex items-center justify-between p-4 transition-all hover:shadow-sm"
                      style={{ background: 'linear-gradient(135deg, rgba(135,123,152,0.08) 0%, rgba(92,155,110,0.08) 100%)' }}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #877B98, #5C9B6E)' }}>
                          <FaPaperPlane className="text-white text-sm" />
                        </div>
                        <div className="text-left">
                          <p className="font-semibold" style={{ color: '#262420' }}>Invite a Friend & Earn Rewards</p>
                          <p className="text-xs" style={{ color: '#8A827A' }}>Send an invite and earn points when they join</p>
                        </div>
                      </div>
                      <FaChevronRight style={{ color: '#CCC5BD' }} />
                    </button>
                  ) : (
                    <div className="p-4" style={{ background: 'linear-gradient(135deg, rgba(135,123,152,0.08) 0%, rgba(92,155,110,0.08) 100%)' }}>
                      <div className="flex items-center gap-2 mb-3">
                        <FaEnvelope style={{ color: '#877B98' }} />
                        <span className="font-semibold" style={{ color: '#262420' }}>Send Referral Invite</span>
                      </div>
                      <form onSubmit={handleSendInvite} className="flex items-center gap-2">
                        <input
                          type="email"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          placeholder="friend@email.com"
                          required
                          className="flex-1 bg-white border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
                          style={{ borderColor: '#CCC5BD', focusRingColor: '#877B98' }}
                        />
                        <button
                          type="submit"
                          disabled={inviteSending || !inviteEmail.trim()}
                          className="px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-50"
                          style={{ background: 'linear-gradient(135deg, #877B98, #5C9B6E)' }}
                        >
                          {inviteSending ? <FaSpinner className="animate-spin" /> : <><FaPaperPlane className="inline mr-1.5" />Send</>}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setShowInviteForm(false); setInviteEmail(''); setInviteResult(null); }}
                          className="px-3 py-2.5 rounded-lg text-sm transition-all hover:bg-gray-100"
                          style={{ color: '#8A827A' }}
                        >
                          Cancel
                        </button>
                      </form>
                    </div>
                  )}
                </div>
              )}

              {/* Tier Progression */}
              <div>
                <h4 className="font-semibold mb-3" style={{ color: '#262420' }}>Membership Tiers</h4>
                <div className="space-y-2">
                  {Object.entries(TIER_STYLES).map(([id, style]) => {
                    const isCurrentTier = id === pointsData?.tier;
                    const tierInfo = {
                      explorer: { min: 0, label: '0 - 999', mult: '1x' },
                      navigator: { min: 1000, label: '1,000 - 4,999', mult: '1.25x' },
                      adventurer: { min: 5000, label: '5,000 - 14,999', mult: '1.5x' },
                      globetrotter: { min: 15000, label: '15,000 - 49,999', mult: '2x' },
                      ambassador: { min: 50000, label: '50,000+', mult: '2.5x' },
                    }[id];

                    return (
                      <div key={id} className="flex items-center gap-3 p-3 rounded-xl transition-all" style={{
                        background: isCurrentTier ? style.gradient : '#F9FAFB',
                        border: isCurrentTier ? `2px solid ${style.border}` : '2px solid transparent',
                      }}>
                        <span className="text-2xl">{style.icon}</span>
                        <div className="flex-1">
                          <p className="font-semibold capitalize" style={{ color: '#262420' }}>
                            {id} {isCurrentTier && <span className="text-xs bg-gray-800 text-white px-2 py-0.5 rounded-full ml-1">Current</span>}
                          </p>
                          <p className="text-xs" style={{ color: '#8A827A' }}>{tierInfo.label} points</p>
                        </div>
                        <span className="font-bold" style={{ color: style.border }}>{tierInfo.mult}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Referrals Tab */}
          {activeTab === 'referrals' && (
            <div className="space-y-4">
              {/* Referral Link */}
              {isEligible && referralLink?.referral_url && (
                <div className="rounded-xl p-4" style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <FaShareAlt style={{ color: '#3B82F6' }} />
                    <span className="font-semibold text-sm" style={{ color: '#1E40AF' }}>Your Referral Link</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={referralLink.referral_url}
                      className="flex-1 bg-white border rounded-lg px-3 py-2 text-sm"
                      style={{ borderColor: '#BFDBFE', color: '#374151' }}
                    />
                    <button
                      onClick={handleCopyLink}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all"
                      style={{ backgroundColor: copied ? '#10B981' : '#3B82F6' }}
                    >
                      {copied ? <><FaCheck className="inline mr-1" />Copied</> : <><FaCopy className="inline mr-1" />Copy</>}
                    </button>
                  </div>
                </div>
              )}

              {/* Invite Form */}
              {isEligible && referralLink && (
                <div className="rounded-xl p-4" style={{ background: 'linear-gradient(135deg, rgba(135,123,152,0.1) 0%, rgba(92,155,110,0.1) 100%)', border: '1px solid #D1FAE5' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <FaPaperPlane style={{ color: '#877B98' }} />
                    <span className="font-semibold" style={{ color: '#262420' }}>Invite a Friend & Earn Rewards</span>
                  </div>
                  <p className="text-sm mb-3" style={{ color: '#68625A' }}>
                    Enter your friend's email and we'll send them a personalized invitation to join ItinerarySeek.
                  </p>
                  {inviteResult && (
                    <div className={`mb-3 p-2.5 rounded-lg text-sm ${inviteResult.success ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                      {inviteResult.success ? <FaCheck className="inline mr-1.5" /> : <FaInfoCircle className="inline mr-1.5" />}
                      {inviteResult.message}
                    </div>
                  )}
                  <form onSubmit={handleSendInvite} className="flex items-center gap-2">
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="friend@email.com"
                      required
                      className="flex-1 bg-white border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2"
                      style={{ borderColor: '#CCC5BD' }}
                    />
                    <button
                      type="submit"
                      disabled={inviteSending || !inviteEmail.trim()}
                      className="px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-50"
                      style={{ background: 'linear-gradient(135deg, #877B98, #5C9B6E)' }}
                    >
                      {inviteSending ? <FaSpinner className="animate-spin" /> : <><FaPaperPlane className="inline mr-1.5" />Send Invite</>}
                    </button>
                  </form>
                </div>
              )}

              <div className="rounded-xl p-4 bg-gray-50">
                <h4 className="font-semibold mb-1" style={{ color: '#262420' }}>How referrals work</h4>
                <ul className="text-sm space-y-1" style={{ color: '#68625A' }}>
                  <li>+100 pts when friend signs up (free)</li>
                  <li>+500 pts when friend subscribes to Solo (after 30 days)</li>
                  <li>+750 pts when friend subscribes to Group (after 30 days)</li>
                  <li>+1,000 pts bonus if friend chooses yearly billing</li>
                </ul>
                <div className="mt-2 p-2 bg-amber-50 rounded-lg">
                  <p className="text-xs text-amber-700">
                    <FaInfoCircle className="inline mr-1" />
                    Max 2,000 pts/month. Subscription rewards require friend to stay on paid plan 30+ days.
                  </p>
                </div>
              </div>

              {referrals.length > 0 ? (
                <div>
                  <h4 className="font-semibold mb-2" style={{ color: '#262420' }}>Your Referrals</h4>
                  {referrals.map((r) => (
                    <div key={r.id} className="flex items-center justify-between py-3 border-b" style={{ borderColor: '#E5E7EB' }}>
                      <div>
                        <p className="font-medium" style={{ color: '#262420' }}>{r.referee_name}</p>
                        <p className="text-xs" style={{ color: '#8A827A' }}>
                          {r.referee_plan} &middot; {r.status === 'pending_upgrade' ? 'awaiting 30-day qualification' : r.status}
                        </p>
                      </div>
                      <div className="text-right">
                        {r.points_awarded > 0 && (
                          <p className="font-semibold text-emerald-600">+{r.points_awarded} pts</p>
                        )}
                        <p className="text-xs" style={{ color: '#8A827A' }}>
                          {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FaUsers className="text-4xl mx-auto mb-3" style={{ color: '#CCC5BD' }} />
                  <p style={{ color: '#8A827A' }}>No referrals yet. Invite a friend above to start earning!</p>
                </div>
              )}
            </div>
          )}

          {/* Redeem Tab */}
          {activeTab === 'redeem' && (
            <div className="space-y-4">
              {redeemResult && (
                <div className={`p-3 rounded-lg text-sm ${redeemResult.success ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                  {redeemResult.message}
                </div>
              )}

              {rewards?.auto_pay_active && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-800">
                  <FaAward className="inline mr-1" /> Auto Pay discount active: 10% off all redemptions!
                </div>
              )}

              {rewards?.rewards?.map((reward) => (
                <div key={reward.id} className="flex items-center justify-between p-4 rounded-xl border transition-all hover:shadow-sm" style={{
                  borderColor: reward.can_afford ? '#D1FAE5' : '#E5E7EB',
                  backgroundColor: reward.can_afford ? '#F0FDF4' : '#FAFAFA',
                }}>
                  <div className="flex-1">
                    <p className="font-medium" style={{ color: '#262420' }}>{reward.name}</p>
                    <p className="text-sm" style={{ color: '#8A827A' }}>
                      {reward.value !== 'Perk' && reward.value !== 'Badge' ? `Value: ${reward.value}` : reward.value}
                    </p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="font-bold" style={{ color: reward.can_afford ? '#059669' : '#8A827A' }}>
                      {reward.points_required.toLocaleString()} pts
                    </p>
                    <button
                      onClick={() => handleRedeem(reward.id)}
                      disabled={!reward.can_afford || !isEligible || redeemingId === reward.id}
                      className="mt-1 px-3 py-1 rounded-lg text-xs font-medium text-white disabled:opacity-40 transition-all"
                      style={{ backgroundColor: reward.can_afford && isEligible ? '#877B98' : '#CCC5BD' }}
                    >
                      {redeemingId === reward.id ? <FaSpinner className="animate-spin inline" /> : 'Redeem'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div>
              {history?.transactions?.length > 0 ? (
                <div className="space-y-0">
                  {history.transactions.map((txn) => (
                    <div key={txn.id} className="flex items-center justify-between py-3 border-b" style={{ borderColor: '#F3F4F6' }}>
                      <div className="flex-1">
                        <p className="text-sm font-medium" style={{ color: '#262420' }}>{txn.description}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs px-2 py-0.5 rounded-full" style={{
                            backgroundColor: txn.type === 'redemption' ? '#FEE2E2' : txn.type === 'expiration' ? '#FEF3C7' : '#D1FAE5',
                            color: txn.type === 'redemption' ? '#991B1B' : txn.type === 'expiration' ? '#92400E' : '#065F46',
                          }}>
                            {txn.type}
                          </span>
                          <span className="text-xs" style={{ color: '#8A827A' }}>
                            {txn.created_at ? new Date(txn.created_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold" style={{ color: txn.points > 0 ? '#059669' : '#DC2626' }}>
                          {txn.points > 0 ? '+' : ''}{txn.points.toLocaleString()}
                        </p>
                        <p className="text-xs" style={{ color: '#8A827A' }}>
                          bal: {txn.balance_after.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  {history.total > history.transactions.length && (
                    <p className="text-center text-sm py-3" style={{ color: '#8A827A' }}>
                      Showing {history.transactions.length} of {history.total} transactions
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FaHistory className="text-4xl mx-auto mb-3" style={{ color: '#CCC5BD' }} />
                  <p style={{ color: '#8A827A' }}>No transactions yet</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TravelPoints;
